<template>
  <div class="modal-wrap" @click.self="!preventClose && close()">
    <div
      class="modal px-4 py-4 px-md-5 py-md-5"
      :class="variant && `modal--${variant}`"
    >
      <div class="modal-close-btn" @click="close">×</div>
      <slot />
    </div>
  </div>
</template>

<script setup>
// emit close
const props = defineProps({
  preventClose: {
    type: Boolean,
    default: false,
  },
  // Opt-in surface treatment, e.g. `neutral`. Callers that pass nothing keep
  // the stock modal surface, so this is additive for every existing usage.
  variant: {
    type: String,
    default: '',
  },
});
const emit = defineEmits(['closeModal']);
function close() {
  emit('closeModal');
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

.modal-wrap {
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1051; // above the exchange offers dock (1040/1041)
  background: main.$black-202;
  width: 100%;
  height: 100%;
  align-content: center;
  align-items: center;
  justify-content: center;
}
/* `variant="neutral"` — opt-in night surface for panels that are mostly
   chrome around a single card (My Positions).

   The problem this solves is real: the stock night modal is #2e3459, the same
   saturated indigo as the card, the header bar and the row inside it, so the
   whole panel lands as one flat blue slab. The first attempt separated those
   layers by draining the chroma out of them — #2b2e38 / #2f323d / #333749,
   roughly a quarter of the blue-lift the rest of the night theme carries — and
   the panel stopped looking like part of the product.

   Layers are separated by LIGHTNESS instead, in the navy family, the same
   ladder the desktop exchange card uses: panel recedes, the group card sits on
   it, the header bar tucks under the card, and the bet rows are the brightest
   thing in the panel because they are the thing you are here to read. Day mode
   and every modal that passes no variant are untouched. */
.modal--neutral {
  [theme='night'] & {
    background: #1c2140;

    :deep(.box-position) {
      background: #262d52;
    }

    /* `!important` matches, rather than escalates: MyPositionSingleBet's own
       `.betNormal` rule is already `background: … !important`, so a plain
       declaration here loses to it no matter how specific this selector is.

       The `:not()` guard is load-bearing, not defensive. MyPositionSingleBet
       applies `betNormal` with the condition
       `outcome !== 'winner' || outcome !== 'loser'`, which is true for EVERY
       value — so a settled row carries `win`/`lose` AND `betNormal` at once.
       Those outcome rules are also `!important` but only `.win[data-v-…]`
       (0,2,0); today they win purely on source order. This selector is
       (0,4,0), so without the guard it outranks them and repaints graded
       wins and losses neutral grey. Verified in the app. */
    :deep(.betNormal:not(.win):not(.lose):not(.refunded)) {
      background: #333c66 !important;
    }
  }
}

.modal {
  position: relative;
  border-radius: 15px;
  background: main.$white_01;
  box-shadow: 0 1px 5px 0 main.$black-101;
  width: 770px;
  overflow-x: auto;
  max-height: calc(100vh - 5rem);
  overflow-y: auto;

  @media screen and (max-width: map.get(main.$grid-breakpoints, md)) {
    width: 100%;
    height: 100%;
    overflow-y: auto;
    box-shadow: none;
  }

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    width: 100%;
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
    box-shadow: none;
  }

  @media screen and (max-width: 375px) {
    width: 100%;
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
    box-shadow: none;
  }
}
.modal-close-btn {
  position: absolute;
  right: 0;
  top: 0;
  padding: 24px;
  line-height: 0px;
  cursor: pointer;
  color: main.$blue-100;
  font-size: 2rem;
  z-index: 10;
  &:hover {
    color: main.$color-primary_01;
  }
  @media screen and (min-width: map.get(main.$grid-breakpoints, md)) {
    line-height: 1rem;
  }
}
</style>
